import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';
import { containedButtonStyle, textButtonStyle } from '@/theme';
import { abyssColors } from '@/theme/colors';
import { useCallback } from 'react';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { PointOfInterestsDocument } from '@/__generated__/graphql';
import { GetPointOfInterestGroupedByWorkpackDocument, GroupPointOfInterestsByWorkpackIdDocument, useRemoveWorkpackIdFromPointOfInterestsMutation } from '@/__generated__/graphql';

type MoveToWorkpackDialogProps = {
  isOpen: boolean;
  handleWorkpackDialogClose: React.Dispatch<React.SetStateAction<boolean>>;
  title: string;
  workpackId: string;
  selectedPointOfInterestIds: string[];
};

export const RemoveFromWorkpackDialog = ({
  isOpen,
  handleWorkpackDialogClose,
  title,
  workpackId,
  selectedPointOfInterestIds,
}: MoveToWorkpackDialogProps) => {
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const [removeWorkpackIdFromPointOfInterestMutation] =
    useRemoveWorkpackIdFromPointOfInterestsMutation();

  const handleMoveFromWorkpack = useCallback(async () => {
    await removeWorkpackIdFromPointOfInterestMutation({
      variables: {
        input: {
          workpackId: workpackId,
          pointOfInterestIds: selectedPointOfInterestIds,
        },
      },
      refetchQueries: [
        PointOfInterestsDocument,
        GroupPointOfInterestsByWorkpackIdDocument,
        GetPointOfInterestGroupedByWorkpackDocument,
        'GetPointOfInterest', // This will ensure the POI details are refetched
      ],
      onCompleted: () => {
        handleWorkpackDialogClose(false);
        showSnackBar('POIs have been removed successfully from workpack', 'success');
      },
      onError: (error) => {
        showSnackBar(`${error.message}`, 'error');
      },
    });
  }, [
    handleWorkpackDialogClose,
    removeWorkpackIdFromPointOfInterestMutation,
    selectedPointOfInterestIds,
    showSnackBar,
    workpackId,
  ]);

  return (
    <Dialog open={isOpen} maxWidth="sm" fullWidth sx={{ radius: 2 }}>
      <DialogTitle sx={{ m: 0, p: 2 }}>{title}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => handleWorkpackDialogClose(false)}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ mb: 4, p: 2 }}>
        <Typography
          sx={{
            fontSize: '1.5rem',
            color: abyssColors.primary[400],
          }}
        >
          Are you sure you want to remove the selected POIs from the workpack?
        </Typography>
      </DialogContent>
      <Divider />
      <Divider orientation="horizontal" />
      <DialogActions>
        <Button
          id="cancel-remove-from-workpack"
          variant="outlined"
          size="small"
          sx={{ mr: 2, ...textButtonStyle }}
          onClick={() => handleWorkpackDialogClose(false)}
        >
          Cancel
        </Button>
        <Button
          id="confirm-remove-from-workpack"
          variant="contained"
          color="primary"
          size="small"
          sx={{ ...containedButtonStyle }}
          onClick={handleMoveFromWorkpack}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};
